/**
 * Curated set of common standard emojis with shortcodes.
 * Used by RichTextInput's `:` autocomplete and the emoji picker.
 *
 * Format kept compact on purpose — this file is bundled with every page.
 * Keywords (third tuple element) help the search match common synonyms.
 *
 * To add more, follow the pattern. Shortcodes are lowercase, no spaces.
 */

export interface StandardEmoji {
  shortcode: string;
  char: string;
  category: EmojiCategory;
  keywords?: string[];
}

export type EmojiCategory =
  | "smileys"
  | "people"
  | "nature"
  | "food"
  | "activities"
  | "travel"
  | "objects"
  | "symbols"
  | "flags";

type Tuple = [string, string, EmojiCategory, string[]?];

const RAW: Tuple[] = [
  // Smileys & emotion
  ["smile", "😄", "smileys", ["happy", "joy", "grin"]],
  ["grinning", "😀", "smileys", ["happy", "joy"]],
  ["laughing", "😆", "smileys", ["laugh", "haha"]],
  ["joy", "😂", "smileys", ["lol", "laugh", "tears"]],
  ["rofl", "🤣", "smileys", ["laugh", "rolling"]],
  ["sweat_smile", "😅", "smileys", ["relief"]],
  ["wink", "😉", "smileys", []],
  ["blush", "😊", "smileys", ["happy"]],
  ["innocent", "😇", "smileys", ["angel"]],
  ["slight_smile", "🙂", "smileys", []],
  ["upside_down", "🙃", "smileys", []],
  ["yum", "😋", "smileys", ["delicious", "tongue"]],
  ["sunglasses", "😎", "smileys", ["cool"]],
  ["heart_eyes", "😍", "smileys", ["love"]],
  ["kissing_heart", "😘", "smileys", ["kiss", "love"]],
  ["star_struck", "🤩", "smileys", ["excited"]],
  ["thinking", "🤔", "smileys", ["hmm"]],
  ["neutral_face", "😐", "smileys", []],
  ["expressionless", "😑", "smileys", []],
  ["zipper_mouth", "🤐", "smileys", ["quiet"]],
  ["roll_eyes", "🙄", "smileys", []],
  ["smirk", "😏", "smileys", []],
  ["unamused", "😒", "smileys", []],
  ["sweat", "😓", "smileys", []],
  ["pensive", "😔", "smileys", ["sad"]],
  ["confused", "😕", "smileys", []],
  ["worried", "😟", "smileys", []],
  ["frowning", "😦", "smileys", []],
  ["anguished", "😧", "smileys", []],
  ["open_mouth", "😮", "smileys", []],
  ["hushed", "😯", "smileys", []],
  ["astonished", "😲", "smileys", ["wow"]],
  ["flushed", "😳", "smileys", []],
  ["pleading", "🥺", "smileys", []],
  ["frowning_face", "☹️", "smileys", ["sad"]],
  ["cry", "😢", "smileys", ["sad", "tear"]],
  ["sob", "😭", "smileys", ["crying"]],
  ["scream", "😱", "smileys", ["shock"]],
  ["confounded", "😖", "smileys", []],
  ["persevere", "😣", "smileys", []],
  ["disappointed", "😞", "smileys", []],
  ["sweat_drops", "💦", "smileys", []],
  ["weary", "😩", "smileys", []],
  ["tired_face", "😫", "smileys", []],
  ["yawning", "🥱", "smileys", []],
  ["triumph", "😤", "smileys", []],
  ["rage", "😡", "smileys", ["angry", "mad"]],
  ["angry", "😠", "smileys", ["mad"]],
  ["face_with_symbols", "🤬", "smileys", ["swearing"]],
  ["mask", "😷", "smileys", ["sick"]],
  ["thermometer_face", "🤒", "smileys", ["sick"]],
  ["nauseated", "🤢", "smileys", []],
  ["vomiting", "🤮", "smileys", []],
  ["sneezing", "🤧", "smileys", []],
  ["hot_face", "🥵", "smileys", []],
  ["cold_face", "🥶", "smileys", []],
  ["dizzy_face", "😵", "smileys", []],
  ["exploding_head", "🤯", "smileys", ["mind blown"]],
  ["cowboy", "🤠", "smileys", []],
  ["partying", "🥳", "smileys", ["party"]],
  ["disguised", "🥸", "smileys", []],
  ["nerd", "🤓", "smileys", []],
  ["monocle", "🧐", "smileys", []],
  ["sleeping", "😴", "smileys", ["zzz"]],
  ["face_with_rolling_eyes", "🙄", "smileys", []],
  ["zany", "🤪", "smileys", ["crazy"]],
  ["money_face", "🤑", "smileys", []],
  ["hugs", "🤗", "smileys", ["hug"]],
  ["face_palm", "🤦", "smileys", ["facepalm"]],
  ["shrug", "🤷", "smileys", []],
  ["clown", "🤡", "smileys", []],
  ["robot", "🤖", "smileys", []],
  ["ghost", "👻", "smileys", []],
  ["skull", "💀", "smileys", []],
  ["alien", "👽", "smileys", []],
  ["poop", "💩", "smileys", []],

  // Hearts
  ["heart", "❤️", "symbols", ["love"]],
  ["orange_heart", "🧡", "symbols", []],
  ["yellow_heart", "💛", "symbols", []],
  ["green_heart", "💚", "symbols", []],
  ["blue_heart", "💙", "symbols", []],
  ["purple_heart", "💜", "symbols", []],
  ["black_heart", "🖤", "symbols", []],
  ["white_heart", "🤍", "symbols", []],
  ["brown_heart", "🤎", "symbols", []],
  ["broken_heart", "💔", "symbols", []],
  ["sparkling_heart", "💖", "symbols", []],
  ["growing_heart", "💗", "symbols", []],
  ["beating_heart", "💓", "symbols", []],
  ["revolving_hearts", "💞", "symbols", []],
  ["two_hearts", "💕", "symbols", []],
  ["heart_decoration", "💟", "symbols", []],
  ["cupid", "💘", "symbols", []],
  ["gift_heart", "💝", "symbols", []],

  // Hand gestures
  ["thumbsup", "👍", "people", ["+1", "yes", "approve"]],
  ["thumbsdown", "👎", "people", ["-1", "no"]],
  ["ok_hand", "👌", "people", []],
  ["wave", "👋", "people", ["hello", "hi", "bye"]],
  ["raised_hand", "✋", "people", []],
  ["raised_hands", "🙌", "people", ["praise"]],
  ["clap", "👏", "people", ["applause"]],
  ["pray", "🙏", "people", ["thanks", "please"]],
  ["fist", "✊", "people", []],
  ["fist_oncoming", "👊", "people", ["punch"]],
  ["fist_left", "🤛", "people", []],
  ["fist_right", "🤜", "people", []],
  ["crossed_fingers", "🤞", "people", ["luck"]],
  ["love_you_gesture", "🤟", "people", []],
  ["metal", "🤘", "people", ["rock"]],
  ["call_me_hand", "🤙", "people", []],
  ["point_left", "👈", "people", []],
  ["point_right", "👉", "people", []],
  ["point_up_2", "👆", "people", []],
  ["point_down", "👇", "people", []],
  ["point_up", "☝️", "people", []],
  ["pinching", "🤏", "people", []],
  ["v", "✌️", "people", ["peace"]],
  ["hand_splayed", "🖐️", "people", []],
  ["vulcan", "🖖", "people", []],
  ["writing_hand", "✍️", "people", []],
  ["muscle", "💪", "people", ["flex"]],

  // People
  ["baby", "👶", "people", []],
  ["child", "🧒", "people", []],
  ["boy", "👦", "people", []],
  ["girl", "👧", "people", []],
  ["man", "👨", "people", []],
  ["woman", "👩", "people", []],
  ["older_man", "👴", "people", []],
  ["older_woman", "👵", "people", []],
  ["family", "👪", "people", []],
  ["couple", "👫", "people", []],

  // Animals & nature
  ["dog", "🐶", "nature", []],
  ["cat", "🐱", "nature", []],
  ["mouse", "🐭", "nature", []],
  ["hamster", "🐹", "nature", []],
  ["rabbit", "🐰", "nature", []],
  ["fox", "🦊", "nature", []],
  ["bear", "🐻", "nature", []],
  ["panda", "🐼", "nature", []],
  ["koala", "🐨", "nature", []],
  ["tiger", "🐯", "nature", []],
  ["lion", "🦁", "nature", []],
  ["cow", "🐮", "nature", []],
  ["pig", "🐷", "nature", []],
  ["frog", "🐸", "nature", []],
  ["monkey", "🐵", "nature", []],
  ["chicken", "🐔", "nature", []],
  ["penguin", "🐧", "nature", []],
  ["bird", "🐦", "nature", []],
  ["duck", "🦆", "nature", []],
  ["eagle", "🦅", "nature", []],
  ["owl", "🦉", "nature", []],
  ["bat", "🦇", "nature", []],
  ["wolf", "🐺", "nature", []],
  ["unicorn", "🦄", "nature", []],
  ["horse", "🐴", "nature", []],
  ["dolphin", "🐬", "nature", []],
  ["whale", "🐳", "nature", []],
  ["fish", "🐟", "nature", []],
  ["shark", "🦈", "nature", []],
  ["octopus", "🐙", "nature", []],
  ["snake", "🐍", "nature", []],
  ["turtle", "🐢", "nature", []],
  ["bug", "🐛", "nature", []],
  ["bee", "🐝", "nature", []],
  ["butterfly", "🦋", "nature", []],
  ["snail", "🐌", "nature", []],
  ["spider", "🕷️", "nature", []],
  ["seedling", "🌱", "nature", []],
  ["evergreen_tree", "🌲", "nature", []],
  ["deciduous_tree", "🌳", "nature", []],
  ["palm_tree", "🌴", "nature", []],
  ["cactus", "🌵", "nature", []],
  ["four_leaf_clover", "🍀", "nature", ["luck"]],
  ["maple_leaf", "🍁", "nature", []],
  ["fallen_leaf", "🍂", "nature", []],
  ["mushroom", "🍄", "nature", []],
  ["sunflower", "🌻", "nature", []],
  ["rose", "🌹", "nature", []],
  ["tulip", "🌷", "nature", []],
  ["cherry_blossom", "🌸", "nature", []],
  ["sun", "☀️", "nature", []],
  ["partly_sunny", "⛅", "nature", []],
  ["cloud", "☁️", "nature", []],
  ["rain_cloud", "🌧️", "nature", []],
  ["thunder_cloud", "⛈️", "nature", []],
  ["snow_cloud", "🌨️", "nature", []],
  ["snowflake", "❄️", "nature", []],
  ["snowman", "⛄", "nature", []],
  ["fire", "🔥", "nature", []],
  ["sparkles", "✨", "nature", []],
  ["star", "⭐", "nature", []],
  ["star2", "🌟", "nature", []],
  ["dizzy", "💫", "nature", []],
  ["boom", "💥", "nature", []],
  ["zap", "⚡", "nature", []],
  ["rainbow", "🌈", "nature", []],
  ["droplet", "💧", "nature", []],
  ["ocean", "🌊", "nature", []],
  ["earth_americas", "🌎", "nature", []],
  ["earth_africa", "🌍", "nature", []],
  ["earth_asia", "🌏", "nature", []],
  ["full_moon", "🌕", "nature", []],
  ["crescent_moon", "🌙", "nature", []],

  // Food & drink
  ["apple", "🍎", "food", []],
  ["green_apple", "🍏", "food", []],
  ["pear", "🍐", "food", []],
  ["orange", "🍊", "food", []],
  ["lemon", "🍋", "food", []],
  ["banana", "🍌", "food", []],
  ["watermelon", "🍉", "food", []],
  ["grapes", "🍇", "food", []],
  ["strawberry", "🍓", "food", []],
  ["peach", "🍑", "food", []],
  ["cherries", "🍒", "food", []],
  ["pineapple", "🍍", "food", []],
  ["coconut", "🥥", "food", []],
  ["kiwi_fruit", "🥝", "food", []],
  ["tomato", "🍅", "food", []],
  ["eggplant", "🍆", "food", []],
  ["avocado", "🥑", "food", []],
  ["broccoli", "🥦", "food", []],
  ["carrot", "🥕", "food", []],
  ["corn", "🌽", "food", []],
  ["hot_pepper", "🌶️", "food", []],
  ["cucumber", "🥒", "food", []],
  ["potato", "🥔", "food", []],
  ["sweet_potato", "🍠", "food", []],
  ["bread", "🍞", "food", []],
  ["croissant", "🥐", "food", []],
  ["bagel", "🥯", "food", []],
  ["pretzel", "🥨", "food", []],
  ["cheese", "🧀", "food", []],
  ["egg", "🥚", "food", []],
  ["fried_egg", "🍳", "food", []],
  ["bacon", "🥓", "food", []],
  ["pancakes", "🥞", "food", []],
  ["waffle", "🧇", "food", []],
  ["hamburger", "🍔", "food", ["burger"]],
  ["fries", "🍟", "food", []],
  ["pizza", "🍕", "food", []],
  ["hotdog", "🌭", "food", []],
  ["sandwich", "🥪", "food", []],
  ["taco", "🌮", "food", []],
  ["burrito", "🌯", "food", []],
  ["salad", "🥗", "food", []],
  ["popcorn", "🍿", "food", []],
  ["sushi", "🍣", "food", []],
  ["ramen", "🍜", "food", []],
  ["spaghetti", "🍝", "food", []],
  ["rice", "🍚", "food", []],
  ["curry", "🍛", "food", []],
  ["dumpling", "🥟", "food", []],
  ["icecream", "🍦", "food", []],
  ["cake", "🍰", "food", []],
  ["birthday_cake", "🎂", "food", []],
  ["cookie", "🍪", "food", []],
  ["chocolate_bar", "🍫", "food", []],
  ["candy", "🍬", "food", []],
  ["lollipop", "🍭", "food", []],
  ["donut", "🍩", "food", []],
  ["coffee", "☕", "food", []],
  ["tea", "🍵", "food", []],
  ["bubble_tea", "🧋", "food", []],
  ["beer", "🍺", "food", []],
  ["beers", "🍻", "food", ["cheers"]],
  ["wine_glass", "🍷", "food", []],
  ["champagne", "🍾", "food", []],
  ["cocktail", "🍸", "food", []],
  ["tropical_drink", "🍹", "food", []],

  // Activities & sports
  ["soccer", "⚽", "activities", ["football"]],
  ["basketball", "🏀", "activities", []],
  ["football", "🏈", "activities", []],
  ["baseball", "⚾", "activities", []],
  ["tennis", "🎾", "activities", []],
  ["volleyball", "🏐", "activities", []],
  ["rugby", "🏉", "activities", []],
  ["8ball", "🎱", "activities", []],
  ["ping_pong", "🏓", "activities", []],
  ["badminton", "🏸", "activities", []],
  ["ice_hockey", "🏒", "activities", []],
  ["bow_and_arrow", "🏹", "activities", []],
  ["fishing_pole", "🎣", "activities", []],
  ["boxing_glove", "🥊", "activities", []],
  ["medal", "🏅", "activities", []],
  ["trophy", "🏆", "activities", ["winner"]],
  ["first_place", "🥇", "activities", ["gold"]],
  ["second_place", "🥈", "activities", ["silver"]],
  ["third_place", "🥉", "activities", ["bronze"]],
  ["dart", "🎯", "activities", []],
  ["video_game", "🎮", "activities", []],
  ["game_die", "🎲", "activities", []],
  ["jigsaw", "🧩", "activities", []],
  ["musical_note", "🎵", "activities", []],
  ["notes", "🎶", "activities", []],
  ["microphone", "🎤", "activities", []],
  ["headphones", "🎧", "activities", []],
  ["guitar", "🎸", "activities", []],
  ["drum", "🥁", "activities", []],
  ["art", "🎨", "activities", []],
  ["clapper", "🎬", "activities", []],
  ["camera", "📷", "activities", []],
  ["movie_camera", "🎥", "activities", []],

  // Travel & places
  ["car", "🚗", "travel", []],
  ["taxi", "🚕", "travel", []],
  ["bus", "🚌", "travel", []],
  ["police_car", "🚓", "travel", []],
  ["fire_engine", "🚒", "travel", []],
  ["ambulance", "🚑", "travel", []],
  ["truck", "🚚", "travel", []],
  ["tractor", "🚜", "travel", []],
  ["bike", "🚲", "travel", ["bicycle"]],
  ["scooter", "🛴", "travel", []],
  ["motorcycle", "🏍️", "travel", []],
  ["airplane", "✈️", "travel", []],
  ["rocket", "🚀", "travel", []],
  ["helicopter", "🚁", "travel", []],
  ["train", "🚂", "travel", []],
  ["bullet_train", "🚄", "travel", []],
  ["ship", "🚢", "travel", []],
  ["boat", "⛵", "travel", []],
  ["anchor", "⚓", "travel", []],
  ["house", "🏠", "travel", []],
  ["office", "🏢", "travel", []],
  ["hospital", "🏥", "travel", []],
  ["school", "🏫", "travel", []],
  ["church", "⛪", "travel", []],
  ["mountain", "⛰️", "travel", []],
  ["beach", "🏖️", "travel", []],
  ["desert", "🏜️", "travel", []],
  ["volcano", "🌋", "travel", []],
  ["camping", "🏕️", "travel", []],
  ["tent", "⛺", "travel", []],

  // Objects
  ["watch", "⌚", "objects", []],
  ["iphone", "📱", "objects", ["phone"]],
  ["computer", "💻", "objects", ["laptop"]],
  ["keyboard", "⌨️", "objects", []],
  ["printer", "🖨️", "objects", []],
  ["floppy_disk", "💾", "objects", ["save"]],
  ["bulb", "💡", "objects", ["idea"]],
  ["flashlight", "🔦", "objects", []],
  ["candle", "🕯️", "objects", []],
  ["book", "📖", "objects", []],
  ["books", "📚", "objects", []],
  ["notebook", "📓", "objects", []],
  ["pencil", "✏️", "objects", []],
  ["pen", "🖊️", "objects", []],
  ["paperclip", "📎", "objects", []],
  ["scissors", "✂️", "objects", []],
  ["lock", "🔒", "objects", []],
  ["unlock", "🔓", "objects", []],
  ["key", "🔑", "objects", []],
  ["hammer", "🔨", "objects", []],
  ["wrench", "🔧", "objects", []],
  ["gear", "⚙️", "objects", ["settings"]],
  ["magnet", "🧲", "objects", []],
  ["telescope", "🔭", "objects", []],
  ["microscope", "🔬", "objects", []],
  ["satellite", "📡", "objects", []],
  ["money", "💰", "objects", []],
  ["dollar", "💵", "objects", []],
  ["credit_card", "💳", "objects", []],
  ["gem", "💎", "objects", ["diamond"]],
  ["balloon", "🎈", "objects", []],
  ["gift", "🎁", "objects", ["present"]],
  ["tada", "🎉", "objects", ["party", "celebrate"]],
  ["confetti_ball", "🎊", "objects", []],
  ["clipboard", "📋", "objects", []],
  ["calendar", "📅", "objects", []],
  ["bookmark", "🔖", "objects", []],
  ["mag", "🔍", "objects", ["search"]],

  // Symbols
  ["check", "✅", "symbols", ["yes"]],
  ["x", "❌", "symbols", ["no"]],
  ["heavy_check", "✔️", "symbols", []],
  ["question", "❓", "symbols", []],
  ["exclamation", "❗", "symbols", []],
  ["warning", "⚠️", "symbols", []],
  ["no_entry", "⛔", "symbols", []],
  ["100", "💯", "symbols", []],
  ["arrow_up", "⬆️", "symbols", []],
  ["arrow_down", "⬇️", "symbols", []],
  ["arrow_left", "⬅️", "symbols", []],
  ["arrow_right", "➡️", "symbols", []],
  ["recycle", "♻️", "symbols", []],
  ["plus", "➕", "symbols", []],
  ["minus", "➖", "symbols", []],
  ["divide", "➗", "symbols", []],
  ["infinity", "♾️", "symbols", []],
  ["male", "♂️", "symbols", []],
  ["female", "♀️", "symbols", []],
];

export const STANDARD_EMOJIS: StandardEmoji[] = RAW.map(([shortcode, char, category, keywords]) => ({
  shortcode,
  char,
  category,
  keywords,
}));

/** Index for fast filtering by shortcode prefix or keyword */
export function searchStandardEmojis(query: string, limit = 30): StandardEmoji[] {
  const q = query.toLowerCase().trim();
  if (!q) return STANDARD_EMOJIS.slice(0, limit);
  const results: StandardEmoji[] = [];
  // Prefer prefix matches first
  for (const e of STANDARD_EMOJIS) {
    if (e.shortcode.startsWith(q)) results.push(e);
    if (results.length >= limit) return results;
  }
  for (const e of STANDARD_EMOJIS) {
    if (results.includes(e)) continue;
    if (e.shortcode.includes(q) || e.keywords?.some((k) => k.includes(q))) {
      results.push(e);
    }
    if (results.length >= limit) return results;
  }
  return results;
}

const BY_SHORTCODE = new Map(STANDARD_EMOJIS.map((e) => [e.shortcode, e.char]));

/**
 * `:tada:` → 🎉, for text that will be rendered as PLAIN text.
 *
 * It lives here rather than in `lib/hooks/useTitle.ts`, where it was written:
 * nothing about it is a hook, and the server needs it too. `generateMetadata`
 * for `/begivenheder/[id]` runs on the server and feeds both the tab title and
 * the unfurled link-preview card, and an event called "Sommerfest :tada:" was
 * showing the literal shortcode in both.
 *
 * AN UNKNOWN SHORTCODE IS DROPPED, not left as `:foo:`. `STANDARD_EMOJIS` is a
 * curated set and a group's CUSTOM emoji is not in it — those are rendered as
 * an image by the cooked pipeline, which a title cannot do, so the choice is
 * between dropping the token and printing markup at the user.
 */
export function resolveEmojiShortcodes(text: string): string {
  return text.replace(/:([a-z0-9_+-]+):/g, (_, code) => BY_SHORTCODE.get(code) ?? "");
}
